// textStep.js
// Step "gfxtext" (3 of 5) of the graphic looks (presentation text-around / text-vow): the visitor writes the
// text, a canvas preview follows every keystroke, the ink colour is chosen. Texts go to the session
// (Storage 'gfxText' / 'gfxInk'), travel in mscp_meta_data to the order item, and gfxCanvas.js draws the
// same layout at print size from them. Looks without a graphic presentation never show this step.
import events from './event.js';
import Storage from './storage.js';
import imageHelper from './imageHelper.js';
import Logger from './logger.js';
import { GFX_PRESENTATIONS, GFX_FIELDS, GFX_INK, GFX_DEFAULT_INK, GFX_FONT, renderGraphic } from './gfxCanvas.js';

export function getGraphicKind() {
    return GFX_PRESENTATIONS[Storage.get('presentation')] || null;
}

/** true when the visitor has written something (the step is done) */
export function hasGraphicText() {
    const t = Storage.get('gfxText') || {};
    return Object.values(t).some((v) => String(v || '').trim() !== '');
}

/** The graphic look's canvas at the given width from the session (image, texts, ink); null when the look has none. */
export async function renderCurrentGraphic(width) {
    const kind = getGraphicKind();
    if (!kind) return null;
    const active = await imageHelper.getActiveImage();
    const url = active?.image?.serverUrl || active?.image?.blobUrl;
    if (!url) return null;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    await img.decode();
    try {
        await document.fonts.load("40px 'Cedarville Cursive'");
        await document.fonts.load("40px 'Kristi'");
    } catch (e) { /* browser cursive */ }
    const saved = Storage.get('gfxText') || {};
    const vals = {};
    GFX_FIELDS[kind].forEach((f) => (vals[f.k] = saved[f.k] != null ? saved[f.k] : ''));
    const ink = (GFX_INK.find((k) => k.v === (Storage.get('gfxInk') || GFX_DEFAULT_INK[kind])) || GFX_INK[0]).c;
    return renderGraphic(kind, { image: img, width, font: kind === 'vow' ? "'Kristi', cursive" : GFX_FONT, ink, ...vals });
}

class TextStep {
    constructor(customizerContentDiv) {
        this.customizerContentDiv = customizerContentDiv;
        this.wrapperDiv = document.createElement('div');
        this.wrapperDiv.classList.add('mscp-gfxtext-wrapper');
        customizerContentDiv.appendChild(this.wrapperDiv);
        this.kind = null;
        this.image = null;
        events.add('stepChanged', (step) => {
            if (step === 'gfxtext') this.render();
        });
        events.add('reInitUpload', () => {
            Storage.remove('gfxText');
            Storage.remove('gfxInk');
        });
    }

    async loadImage() {
        const active = await imageHelper.getActiveImage();
        const url = active?.image?.serverUrl || active?.image?.blobUrl;
        if (!url) return null;
        if (this.image && this.image.dataset.src === url) return this.image;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.dataset.src = url;
        img.src = url;
        try {
            await img.decode();
        } catch (e) {
            Logger.warn('Text step: image could not be decoded for the preview.', e);
            return null;
        }
        this.image = img;
        return img;
    }

    values() {
        const saved = Storage.get('gfxText') || {};
        const out = {};
        GFX_FIELDS[this.kind].forEach((f) => (out[f.k] = saved[f.k] != null ? saved[f.k] : ''));
        return out;
    }

    async draw() {
        const img = await this.loadImage();
        if (!img || !this.preview) return;
        try {
            await document.fonts.load("40px 'Cedarville Cursive'");
            await document.fonts.load("40px 'Kristi'");
        } catch (e) { /* fall back to the browser cursive */ }
        // Empty fields show the sketch's sample text faded (preview only: nothing faded is saved or printed).
        const sample = this.values();
        const ghost = {};
        GFX_FIELDS[this.kind].forEach((f) => { if (sample[f.k] === '') { sample[f.k] = f.def; ghost[f.k] = true; } });
        const ink = (GFX_INK.find((k) => k.v === (Storage.get('gfxInk') || GFX_DEFAULT_INK[this.kind])) || GFX_INK[0]).c;
        const canvas = renderGraphic(this.kind, { image: img, width: 640, font: this.kind === 'vow' ? "'Kristi', cursive" : GFX_FONT, ink, ghost, ...sample });
        canvas.className = 'mscp-gfxtext-preview';
        this.preview.replaceWith(canvas);
        this.preview = canvas;
    }

    async render() {
        this.kind = getGraphicKind();
        this.wrapperDiv.innerHTML = '';
        if (!this.kind) return;
        // Only this look's fields are kept: text of another graphic look must not ride along.
        const keys = GFX_FIELDS[this.kind].map((f) => f.k);
        const saved = Storage.get('gfxText') || {};
        const have = {};
        keys.forEach((k) => { if (saved[k] != null) have[k] = saved[k]; });
        if (JSON.stringify(have) !== JSON.stringify(saved)) Storage.save('gfxText', have);
        if (!Storage.get('gfxInk')) Storage.save('gfxInk', GFX_DEFAULT_INK[this.kind]);
        events.trigger('updateNavigation');
        this.preview = document.createElement('canvas');
        this.wrapperDiv.appendChild(this.preview);

        const vals = this.values();
        GFX_FIELDS[this.kind].forEach((f) => {
            const row = document.createElement('div');
            row.className = 'mscp-gfxtext-field';
            const label = document.createElement('label');
            label.textContent = f.label;
            const input = document.createElement(f.area ? 'textarea' : 'input');
            if (f.area) input.rows = 5; else input.type = 'text';
            input.maxLength = f.max;
            input.placeholder = f.def;
            input.id = 'mscp-gfxtext-' + f.k;
            label.htmlFor = input.id;
            input.value = vals[f.k];
            const count = document.createElement('span');
            count.className = 'mscp-gfxtext-count';
            const upd = () => {
                count.textContent = input.value.length + '/' + f.max;
                count.classList.toggle('full', input.value.length >= f.max);
            };
            upd();
            input.addEventListener('input', () => {
                upd();
                const t = { ...(Storage.get('gfxText') || {}), [f.k]: input.value.slice(0, f.max) };
                Storage.save('gfxText', t);
                Storage.save('thumbnailUrlDirty', true); // the thumbnail carries the text: draw it again
                this.draw();
                events.trigger('updateNavigation');
            });
            row.append(label, input, count);
            this.wrapperDiv.appendChild(row);
        });

        const inks = document.createElement('div');
        inks.className = 'mscp-gfxtext-inks';
        const current = Storage.get('gfxInk') || GFX_DEFAULT_INK[this.kind];
        GFX_INK.forEach((k) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.title = k.label;
            b.setAttribute('aria-label', k.label);
            b.style.background = k.c;
            b.classList.toggle('active', k.v === current);
            b.setAttribute('aria-pressed', String(k.v === current));
            b.addEventListener('click', () => {
                Storage.save('gfxInk', k.v);
                Storage.save('thumbnailUrlDirty', true);
                inks.querySelectorAll('button').forEach((x) => { x.classList.toggle('active', x === b); x.setAttribute('aria-pressed', String(x === b)); });
                this.draw();
            });
            inks.appendChild(b);
        });
        this.wrapperDiv.appendChild(inks);
        await this.draw();
    }
}

export default TextStep;
